import { useState, useEffect } from "react";
import axios from "axios";
import { FileText, Clock, CheckCircle, XCircle, Download, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import Navigation from "../components/Navigation";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubmissionsPage = ({ user }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Error loading submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submissionId) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/admin/submissions/${submissionId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission approved and published!');
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error approving submission');
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveAll = async () => {
    const total = submissions.length;
    if (total === 0) {
      toast.info('No pending submissions to approve');
      return;
    }
    if (!window.confirm(
      `Approve all ${total} pending submission${total === 1 ? '' : 's'}? ` +
      `Each one will be published to the archive and its author notified by email.`
    )) return;

    setProcessing(true);
    try {
      const token = localStorage.getItem('access_token');
      const resp = await axios.post(
        `${API}/admin/submissions/approve-all-pending`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
      );
      const { approved, skipped, failed } = resp.data || {};
      if (approved > 0) {
        toast.success(
          `Approved ${approved} submission${approved === 1 ? '' : 's'}` +
          (skipped ? ` — ${skipped} skipped (no permission)` : '') +
          (failed ? ` — ${failed} failed` : ''),
          { duration: 8000 }
        );
      } else if (skipped > 0 || failed > 0) {
        toast.warning(`No submissions approved (${skipped} skipped, ${failed} failed)`);
      } else {
        toast.info('No pending submissions to approve');
      }
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error approving submissions');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSubmission) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('reason', rejectReason);
      
      await axios.post(`${API}/admin/submissions/${selectedSubmission.id}/reject`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error rejecting submission');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectDialog = (submission) => {
    setSelectedSubmission(submission);
    setShowRejectDialog(true);
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <p className="text-slate-600">Admin access required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
              Pending Submissions
            </h1>
            <p className="text-slate-600 mt-1">Review and approve client document submissions</p>
          </div>
          {!loading && submissions.length > 0 && (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleApproveAll}
              disabled={processing}
              data-testid="approve-all-pending-btn"
            >
              {processing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>
              ) : (
                <>Approve all ({submissions.length})</>
              )}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">All caught up!</h3>
            <p className="text-slate-500">No pending submissions to review.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map((submission) => (
              <div key={submission.id} className="bg-white border border-slate-200 rounded-md p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <h3 className="text-lg font-semibold text-slate-900">{submission.title}</h3>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                        submission.requires_payment 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {submission.requires_payment ? 'Review & publishing (75$)' : 'Load only (Free)'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm text-slate-500 mb-4">
                      <div>
                        <span className="font-medium text-slate-700">Author:</span><br/>
                        {submission.author_name}
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Category:</span><br/>
                        <span className="capitalize">{submission.category}</span>
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Submission Type:</span><br/>
                        <span className="capitalize">{submission.submission_type}</span>
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Words:</span><br/>
                        {submission.word_count?.toLocaleString()}
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Submitted:</span><br/>
                        {new Date(submission.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className="text-sm text-slate-500">
                      <span className="font-medium text-slate-700">Submitted by:</span> {submission.submitted_by_email}
                    </div>
                    
                    {submission.payment_status === 'paid' && (
                      <div className="mt-2 text-sm text-green-600">
                        ✓ Payment received: ${submission.payment_amount?.toFixed(2)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col items-end gap-2 ml-4">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('access_token');
                            const response = await axios.get(`${API}/admin/submissions/${submission.id}/file`, {
                              headers: { Authorization: `Bearer ${token}` },
                              responseType: 'blob',
                            });
                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = submission.filename || `${submission.title || 'submission'}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Download error:', error);
                            toast.error(error.response?.data?.detail || 'Error downloading submission file');
                          }
                        }}
                        className="border-slate-300 hover:bg-slate-50 text-slate-700"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('access_token');
                            const response = await axios.get(`${API}/admin/submissions/${submission.id}/similarity`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            const data = response.data || {};
                            const percent = data.similarity != null ? Math.round(data.similarity * 100) : 0;
                            if (data.matched_item_id) {
                              toast.info(
                                `Highest similarity: ${percent}% with \"${data.matched_title || 'Untitled'}\"`,
                                { duration: 7000 }
                              );
                            } else {
                              toast.info(`No significant similarity found (score ${percent}%)`, { duration: 5000 });
                            }
                          } catch (error) {
                            console.error('Similarity error:', error);
                            toast.error(error.response?.data?.detail || 'Error checking similarity for this submission');
                          }
                        }}
                        className="border-slate-300 hover:bg-slate-50 text-slate-700"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Check similarity
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const token = localStorage.getItem('access_token');
                          if (!token) {
                            toast.error('Authentication required');
                            return;
                          }
                          const payload = {
                            submission: {
                              id: submission.id,
                              title: submission.title,
                              author_name: submission.author_name,
                              submitted_by_email: submission.submitted_by_email,
                              category: submission.category,
                              submission_type: submission.submission_type,
                              word_count: submission.word_count,
                            },
                          };
                          window.open(`/admin/submissions/${submission.id}/similarity`, '_blank', 'noopener,noreferrer');
                        }}
                        className="border-slate-300 hover:bg-slate-50 text-slate-700"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Open comparison
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleApprove(submission.id)}
                        disabled={processing}
                        className="text-green-600 border-green-300 hover:bg-green-50"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openRejectDialog(submission)}
                        disabled={processing}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Submission</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Rejecting: <strong>{selectedSubmission?.title}</strong>
              </p>
              <div>
                <label className="block text-sm font-medium mb-2">Reason for rejection (optional)</label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleReject}
                  disabled={processing}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {processing ? 'Rejecting...' : 'Reject Submission'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminSubmissionsPage;
